import { apiFetch } from "./client";
import type { Page } from "./admin";

/** Firma koju bi dobavljač iz registra mogao biti. Uvek imenom — šifra čitaocu ne znači ništa. */
export type ImportCandidate = { companyId: string; companyName: string };

/**
 * Predlog otkupnog ugovora pročitan iz klijentovog registra.
 *
 * Svaki uslov sme da bude prazan, i prazno znači „nije napisano", nikad nula. Mereno nad registrom od
 * 171 ugovora: cena stoji na 81 % redova, sidro roka plaćanja na 50 %, paritet na 8 %. `sourceText` je
 * ćelija iz koje je sve pročitano i ona je ono prema čemu čitalac proverava.
 */
export type ContractProposal = {
  id: string;
  sourceFile: string;
  sourceSheet: string;
  sourceRow: number;
  sourceText: string;
  supplierName: string;
  harvestYear: number;
  /** Šta je predmet dogovora. Iz izvora, ne pretpostavljeno: u današnjoj evidenciji uvek soja. */
  culture: string;
  contractDate: string | null;
  quantityT: number | null;
  pricePerT: number | null;
  priceCurrency: string | null;
  parity: string | null;
  loadingPlace: string | null;
  loadingFrom: string | null;
  loadingTo: string | null;
  loadingNote: string | null;
  paymentDays: number | null;
  paymentBase: "LOADING" | "DELIVERY" | "INVOICE" | "FIXED_DATE" | null;
  paymentDueDate: string | null;
  candidates: ImportCandidate[];
  /**
   * `MAPPED` znaci: firma izabrana, ugovora jos nema. Ugovori se prave skupno, kad uparivanje bude
   * gotovo — zato je to zasebno stanje a ne medjukorak u `CONFIRMED`.
   */
  status: "PENDING" | "MAPPED" | "CONFIRMED" | "REJECTED";
  resolvedCompanyId: string | null;
  resolvedCompanyName: string | null;
  createdContractId: string | null;
  rejectedReason: string | null;
  resolvedAt: string | null;
};

/** Iz kog izvora predlog dolazi. Izvora je dvoje, pa je ovo i filter na ekranu. */
export type ProposalSource = "ALL" | "ERP" | "XLS";

/**
 * Jedan uparen predlog koji nije mogao da postane ugovor, i zašto.
 *
 * Red se imenuje onim čime ga čitalac traži u izvoru — imenom dobavljača i rednim brojem reda —
 * jer šifra predloga mu ne znači ništa, a red koji je preskočen mora da se može naći i dopisati.
 */
export type SkippedProposal = {
  proposalId: string;
  sourceRow: number;
  supplierName: string;
  /** `NEMA_KOLICINU`, `NEMA_FIRMU`, ili šifra greške koju je server vratio nad tim redom. */
  razlog: string;
};

/**
 * Ishod skupnog pravljenja ugovora — isti oblik za presek i za pokretanje.
 *
 * Brojevi se ne izvode jedan iz drugog: `napravljeno` su ugovori koji postoje (ili bi nastali),
 * a `preskoceno` nosi razlog po redu. Zbir izveden oduzimanjem prećutao bi red koji je pao iz
 * trećeg razloga.
 */
export type BulkContractOutcome = {
  napravljeno: number;
  preskoceno: SkippedProposal[];
};

export const procurementImportsApi = {
  /**
   * Jedna strana predloga.
   *
   * Spisak se ne povlači ceo: posle uvoza iz registra ugovora starog ERP-a ima ih 692, a biće ih više
   * sa svakim rodom. Strana je dvadeset pet redova, isto kao podrazumevana na backendu.
   */
  list: (status = "PENDING", source: ProposalSource = "ALL", page = 0, size = 25) =>
    apiFetch<Page<ContractProposal>>(
      `/procurement/imports?status=${encodeURIComponent(status)}` +
        (source === "ALL" ? "" : `&source=${source}`) +
        `&page=${page}&size=${size}`,
    ),
  tally: () => apiFetch<Record<string, number>>("/procurement/imports/tally"),
  /** Upari predlog sa firmom. Ugovor se NE pravi — to je skupni korak, posle. */
  map: (id: string, companyId: string) =>
    apiFetch<void>(`/procurement/imports/${id}/map`, { method: "POST", body: { companyId } }),
  unmap: (id: string) => apiFetch<void>(`/procurement/imports/${id}/unmap`, { method: "POST" }),

  /**
   * Koliko bi ugovora nastalo i koji redovi ne mogu — ne menja ništa.
   *
   * Ekran ovo traži pre pokretanja, jer skupno pravljenje nije radnja koja se poništava jednim
   * klikom: posle nje u registru stoji petsto osamdeset nacrta.
   */
  mappedPreview: () => apiFetch<BulkContractOutcome>("/procurement/imports/mapped-preview"),
  /**
   * Napravi ugovore od SVIH uparenih predloga koji imaju količinu, odjednom.
   *
   * Posao traje, jer je redova petsto osamdeset. Idempotentan je po predlogu: red koji je već
   * postao ugovor nosi `CONFIRMED` i drugi prolaz ga preskače, pa ponovno pokretanje ne pravi
   * drugi ugovor nad istim redom.
   */
  confirmMapped: () =>
    apiFetch<BulkContractOutcome>("/procurement/imports/confirm-mapped", { method: "POST" }),

  confirm: (id: string, companyId: string) =>
    apiFetch<{ contractId: string }>(`/procurement/imports/${id}/confirm`, {
      method: "POST",
      body: { companyId },
    }),
  reject: (id: string, reason: string) =>
    apiFetch<void>(`/procurement/imports/${id}/reject`, {
      method: "POST",
      body: { reason },
    }),
};
